import { useCallback, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
/**
 * Custom hook for managing URL search parameters
 * Provides a clean API for reading and updating URL parameters
 *
 * Every mutator forwards the current `location.state` to `setSearchParams`, since each
 * param change issues a navigation that would otherwise wipe the navigation state.
 *
 * @example
 * ```tsx
 * const { params, setParam, updateParams, deleteParam } = useURLManager();
 *
 * // Read params
 * const section = params.section; // or use getParam('section')
 *
 * // Set single param
 * setParam('modal', 'edit_community');
 *
 * // Update multiple params
 * updateParams({ modal: 'edit_community', editId: '123' });
 *
 * // Delete param
 * deleteParam('modal');
 * ```
 */
export const useURLManager = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { hash, state } = useLocation();
    // Convert URLSearchParams to plain object for easier access
    const params = useMemo(() => {
        const result = {};
        searchParams.forEach((value, key) => {
            result[key] = value;
        });
        return result;
    }, [searchParams]);
    const getParam = useCallback((key) => {
        return searchParams.get(key);
    }, [searchParams]);
    const setParam = useCallback((key, value, options = {}) => {
        var _a;
        const newParams = new URLSearchParams(searchParams);
        newParams.set(key, value);
        setSearchParams(newParams, { replace: (_a = options.replace) !== null && _a !== void 0 ? _a : false, state });
    }, [searchParams, setSearchParams, state]);
    const updateParams = useCallback((updates, options = {}) => {
        var _a;
        const newParams = new URLSearchParams(searchParams);
        Object.entries(updates).forEach(([key, value]) => {
            if (value === null || value === undefined) {
                newParams.delete(key);
            }
            else {
                newParams.set(key, value);
            }
        });
        setSearchParams(newParams, { replace: (_a = options.replace) !== null && _a !== void 0 ? _a : false, state });
    }, [searchParams, setSearchParams, state]);
    const deleteParam = useCallback((key, options = {}) => {
        var _a;
        const newParams = new URLSearchParams(searchParams);
        newParams.delete(key);
        setSearchParams(newParams, { replace: (_a = options.replace) !== null && _a !== void 0 ? _a : false, state });
    }, [searchParams, setSearchParams, state]);
    const deleteParams = useCallback((keys, options = {}) => {
        var _a;
        const newParams = new URLSearchParams(searchParams);
        keys.forEach((key) => newParams.delete(key));
        setSearchParams(newParams, { replace: (_a = options.replace) !== null && _a !== void 0 ? _a : false, state });
    }, [searchParams, setSearchParams, state]);
    const clearParams = useCallback((options = {}) => {
        var _a;
        setSearchParams(new URLSearchParams(), { replace: (_a = options.replace) !== null && _a !== void 0 ? _a : false, state });
    }, [setSearchParams, state]);
    const hasParam = useCallback((key) => {
        return searchParams.has(key);
    }, [searchParams]);
    const hasParamValue = useCallback((key, value) => {
        return searchParams.get(key) === value;
    }, [searchParams]);
    return {
        params,
        hash,
        getParam,
        setParam,
        updateParams,
        deleteParam,
        deleteParams,
        clearParams,
        hasParam,
        hasParamValue,
    };
};
